import { makeStyles } from "tss-react/mui";
import { MiBuildLogoAlt } from "@miplatform/assets";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 *
 * @param {Object} props
 * @param {boolean} [props.open=true] - Whether the placeholder area should be displayed
 * @param {string} [props.text] - Main text to display in the placeholder area
 * @param {string} [props.helperText] - Optional helper text to display below the main text
 * @param {boolean} [props.showLogo=false] - Whether to show the logo above the text
 * @param {Object} [props.containerStyle] - Optional styles to apply to the container
 * @param {Object} [props.logoStyle] - Optional styles to apply to the logo
 * @param {Array} [props.buttons] - Optional array of button configurations to display below the text
 * @param {ReactNode} [props.iconComponent] - Optional icon component to display above the text instead of the logo
 * @param {string} [props.bgColor] - Optional background color for the placeholder area
 * @returns
 */
export function MiPlaceholderArea({
  open = true,
  text = "",
  helperText,
  showLogo = false,
  containerStyle,
  iconComponent,
  buttons = [],
  bgColor,
  logoStyle,
}) {
  const { classes, theme } = useStyles({ bgColor });

  if (!open) {
    return null;
  }

  return (
    <Grid container style={containerStyle} className={classes.root}>
      {showLogo || !!iconComponent ? (
        <Grid item style={{ marginBottom: theme.spacing(3) }}>
          {iconComponent || (
            <img src={MiBuildLogoAlt} alt="Logo" style={logoStyle} />
          )}
        </Grid>
      ) : null}
      {!!text && (
        <Grid item>
          <Typography variant="HD4" className={classes.mainText}>
            {text}
          </Typography>
        </Grid>
      )}
      {!!helperText && (
        <Grid item>
          <Typography variant="BD3" className={classes.helperText}>
            {helperText}
          </Typography>
        </Grid>
      )}
      {buttons.length > 0 ? (
        <Grid
          item
          sx={{
            marginTop: theme.spacing(2),
          }}
        >
          <Box display="flex" flexDirection="row" gap={2}>
            {buttons.map((button, index) => (
              <Box key={button.title + index} className={classes.btnContainer}>
                <MiButton
                  title={button.title}
                  variant={button.variant}
                  onClick={button.onClick}
                  startIcon={{ path: button.startIcon }}
                  endIcon={{ path: button.endIcon }}
                  disabled={button.disabled}
                />
                {button.tooltip && (
                  <Tooltip title={button.tooltip.text}>
                    <MiIcon
                      path={button.tooltip.path}
                      color={button.tooltip.color || theme.palette.grey.medium}
                      size={button.tooltip.size}
                    />
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        </Grid>
      ) : null}
    </Grid>
  );
}
const useStyles = makeStyles()((theme, { bgColor }) => ({
  root: {
    height: "100%",
    backgroundColor: bgColor || theme.palette.green.light,
    borderRadius: 10,
    padding: theme.spacing(2),
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  mainText: {
    color: theme.palette.text.primary,
    fontWeight: theme.fontWeight.medium,
    fontSize: theme.fontSize.large,
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  btnContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));
